import React from 'react';
import { CaseAttributionData, couponLabel, referralLabel } from './types';
import './CaseAttribution.css';

interface CaseAttributionProps {
  attribution?: CaseAttributionData;
  /**
   * `row` is the cases table, where the cell is one grid track wide and the
   * badges have to stay on one line. `stacked` is the case detail card, which
   * has the room to spell the partner and the discount out in full.
   */
  variant?: 'row' | 'stacked';
}

/**
 * The acquisition badges for a case: which partner's link brought the customer
 * in, and which coupon they checked out with. A case can carry both, one, or
 * neither; "Direct" is a real answer and not an empty state, so it is rendered
 * rather than left blank.
 */
const CaseAttribution: React.FC<CaseAttributionProps> = ({ attribution, variant = 'row' }) => {
  const referral = attribution?.referral;
  const coupon = attribution?.coupon;

  if (!referral && !coupon) {
    return (
      <span className="case-attribution case-attribution--direct" data-testid="case-attribution">
        Direct
      </span>
    );
  }

  return (
    <span
      className={`case-attribution case-attribution--${variant}`}
      data-testid="case-attribution"
    >
      {referral && (
        <span
          className="attribution-badge attribution-badge--referral"
          title={
            referral.partnerName
              ? `Referred by ${referral.partnerName} (code ${referral.code})`
              : `Referral code ${referral.code} — no partner matched`
          }
        >
          <span className="attribution-badge-kind">Referral</span>
          <span className="attribution-badge-value">{referralLabel(referral)}</span>
        </span>
      )}
      {coupon && (
        <span className="attribution-badge attribution-badge--coupon" title={couponLabel(coupon)}>
          <span className="attribution-badge-kind">Coupon</span>
          <span className="attribution-badge-value">{coupon.code || coupon.name || 'Discount'}</span>
        </span>
      )}
      {variant === 'stacked' && coupon && coupon.label && coupon.label !== coupon.code && (
        <span className="attribution-detail">{coupon.label}</span>
      )}
      {variant === 'stacked' && referral?.partnerEmail && (
        <span className="attribution-detail">{referral.partnerEmail}</span>
      )}
    </span>
  );
};

export default CaseAttribution;
